import { Button } from "@/components/ui/baseUi/button"
import { ErrorView } from "@/components/ui/layout/error/errorView"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { shortenText } from "@/modules/utils/stringUtils"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { CustomerHistoryEntity } from "@modules/finances/entities/customerHistoryEntity"
import { useTransactionsHistory, UseTransactionsHistoryKeys } from "@modules/finances/hooks/useTransactionsHistory"
import { BoxTransactionsDetailsLayout } from "@modules/finances/transactions/components/boxTransactionDetailsLayout"
import { UserTransactionsDetailsLayout } from "@modules/finances/transactions/components/userTransactionsDetailsLayout"
import { ApiFinanceType } from "@modules/finances/types/financesType"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HUDatatable } from "@modules/ui/components/dataTables/huDatatable"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { formatDateWithHyphen } from "@modules/utils/dateUtils"
import { createColumnHelper } from "@tanstack/react-table"
import dayjs from "dayjs"
import { ChevronRightIcon } from "lucide-react"
import { Suspense, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"

type TransactionHistoryDetailsProps = {
  className?: string
  stripeCustomerId: string
  apiFinanceType: ApiFinanceType
} & React.ComponentProps<"div">

const columnHelper = createColumnHelper<CustomerHistoryEntity>()

export function TransactionHistoryDetails({
  stripeCustomerId,
  apiFinanceType,
  className,
}: TransactionHistoryDetailsProps) {
  const { sidebarNavigateTo } = useSidebar()
  const language = useLanguage()
  const [selectedTransaction, setSelectedTransaction] = useState<CustomerHistoryEntity>()

  const {
    data: transactionsHistory,
    pageSize,
    setPageSize,
    handlePreviousPage,
    handleNextPage,
    hasNextPage,
    hasPreviousPage,
  } = useTransactionsHistory(
    apiFinanceType === ApiFinanceType.USER ? UseTransactionsHistoryKeys.USER : UseTransactionsHistoryKeys.BOX,
    stripeCustomerId,
  )

  const onSelectedTransaction = async (e: CustomerHistoryEntity) => {
    await sidebarNavigateTo({
      content:
        apiFinanceType === ApiFinanceType.BOX ? (
          <ErrorBoundary fallback={<ErrorView />}>
            <Suspense fallback={<LoadingSpinner />}>
              <BoxTransactionsDetailsLayout transactionId={e.transactionId} />
            </Suspense>
          </ErrorBoundary>
        ) : (
          <ErrorBoundary fallback={<ErrorView />}>
            <Suspense fallback={<LoadingSpinner />}>
              <UserTransactionsDetailsLayout transactionId={e.transactionId} />
            </Suspense>
          </ErrorBoundary>
        ),
      onHide: () => {
        setSelectedTransaction(undefined)
        return Promise.resolve(true)
      },
      replaceAllComponents: true,
    })
    setSelectedTransaction(e)
  }

  const columns = [
    columnHelper.accessor("date", {
      cell: ({ row: { original: transaction } }) => (
        <Text fontStyle="TS" style={{ whiteSpace: "nowrap" }}>
          {formatDateWithHyphen(dayjs(transaction.date).format("L"))}
        </Text>
      ),
      meta: {
        headerClassName: "w-2/8",
        className: "w-2/8",
      },
    }),
    columnHelper.accessor("name", {
      cell: ({ row: { original: transaction } }) =>
        transaction.name ? (
          <Text fontStyle="BM" className="overflow-ellipsis">{`${shortenText(transaction.name, 40)}`}</Text>
        ) : (
          <span />
        ),
      meta: {
        headerClassName: "w-3/8",
        className: "w-3/8",
      },
    }),
    columnHelper.accessor("amount", {
      cell: ({ row: { original: transaction } }) => (
        <Text fontStyle="BM" className="pl-1">
          {formatCurrency(transaction.amount, transaction.currency, language)}
        </Text>
      ),
      meta: {
        headerClassName: "w-2/8",
        className: "w-2/8",
      },
    }),
    columnHelper.display({
      id: "actions",
      cell: ({ row: { original: transaction } }) => (
        <Button
          variant="ghost"
          size="iconSm"
          onClick={() => {
            void onSelectedTransaction(transaction)
          }}
        >
          <ChevronRightIcon size={16} />
        </Button>
      ),
      meta: {
        headerClassName: "w-1/8",
        className: "w-1/8",
      },
    }),
  ]

  return (
    <div className={cn("flex grow flex-col gap-4 pb-4", className)}>
      <HUDatatable
        data={transactionsHistory}
        columns={columns}
        hideHeader
        pageSize={pageSize}
        setPageSize={setPageSize}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        handleRowSelectionChange={(row: CustomerHistoryEntity) => void onSelectedTransaction(row)}
        selectedRowIndex={transactionsHistory.findIndex(
          (transaction) => transaction.transactionId === selectedTransaction?.transactionId,
        )}
        hideFooter={transactionsHistory.length === 0}
        className="*:table-fixed"
      />

      {transactionsHistory.length === 0 && (
        <Text fontStyle="BM" className="mt-8 text-center">
          {word("global.no_result")}
        </Text>
      )}
    </div>
  )
}
